import { CheckIcon, TrashIcon } from '@heroicons/react/24/solid'
import { RankingInfo } from '@tanstack/match-sorter-utils'
import {
    FilterFn,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    KeywordView,
    useDeleteKeywordMutation,
    useGetKeywordsForAccountQuery
} from '../../api/client'
import { toastError } from '../../components/Notification'
import SearchInput from '../../components/SearchInput'
import Spinner from '../../components/Spinner'
import SimpleLink from '../../components/links/SimpleLink'
import AddDefaultKeywordsButton from '../../components/moderation/AddDefaultKeywordsButton'
import SimplePagination from '../../components/table/SimplePagination'
import SimpleTable from '../../components/table/SimpleTable'
import { fuzzyFilter, fuzzySort } from '../../components/table/helpers'

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const columnHelper = createColumnHelper<KeywordView>()

interface KeywordListTableProps {
    accountId: number
    admin: boolean
    keywordsEnabled: boolean
}

function KeywordListTable({ accountId, admin, keywordsEnabled }: KeywordListTableProps) {
    const location = useLocation()

    const [globalFilter, setGlobalFilter] = useState('')
    const [data, setData] = useState<KeywordView[]>([])
    const [keywordsHidden, setKeywordsHidden] = useState(true)

    const {
        data: getKeywordsData,
        loading: getKeywordsLoading,
        refetch: refetchKeywords
    } = useGetKeywordsForAccountQuery({
        variables: { params: { accountId: accountId } }
    })

    const [deleteKeywordMutation, { loading: deleteKeywordLoading }] = useDeleteKeywordMutation({
        onError: (error) => {
            toastError(error.message)
        }
    })

    const handleKeywordDefaultsAdded = async () => {
        await refetchKeywords()
    }

    // When the location changes, e.g. navigating back from creating a new team, then we should refetch.
    useEffect(
        () => {
            refetchKeywords()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname]
    )

    function DeleteCell(info: any) {
        const keyword = info.row.original as KeywordView
        return (
            <TrashIcon
                className="h-5 w-5 text-primary-500 hover:text-primary-700 cursor-pointer"
                onClick={async () => {
                    await deleteKeywordMutation({
                        variables: { keywordId: keyword.id }
                    })
                    await refetchKeywords()
                }}
            />
        )
    }

    const columns = [
        columnHelper.accessor(
            (row) =>
                [row.keyword, row.classifierDetailsDisplayName]
                    .filter((value) => Boolean(value))
                    .join(' '),
            {
                id: 'keyword',
                header: () => 'Keyword',
                cell: (info) => {
                    const keyword = info.row.original.keyword
                    const type = info.row.original.classifierDetailsDisplayName
                    const description = info.row.original.description
                    return (
                        <div className="flex items-center gap-3">
                            <div className="min-w-0">
                                <div className="font-medium text-gray-900">
                                    "{keyword}"
                                    <span className="ml-3">
                                        {type && (
                                            <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                                {type}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="flex gap-3">
                                    {description && (
                                        <span className="text-sm text-gray-500">{description}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                },
                filterFn: 'fuzzy',
                sortingFn: fuzzySort
            }
        ),
        columnHelper.accessor('regex', {
            header: () => 'Regex',
            cell: (info) =>
                info.getValue() ? (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                ) : (
                    <span className="text-gray-400">-</span>
                )
        }),
        columnHelper.accessor('allowableRegex', {
            header: () => 'Exceptions',
            cell: (info) => (
                <span className="whitespace-pre-wrap">
                    {info.getValue() ? `"${info.getValue()}"` : ''}
                </span>
            )
        }),
        columnHelper.display({
            id: 'delete',
            cell: (props) => <DeleteCell row={props.row} />
        })
    ]

    useEffect(() => {
        if (getKeywordsData?.getKeywordsForAccount) {
            setData(getKeywordsData?.getKeywordsForAccount)
        }
    }, [getKeywordsData])

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter
        },
        state: {
            globalFilter
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel()
    })

    const isLoading = getKeywordsLoading || deleteKeywordLoading
    const hasKeywords = data.length > 0

    if (!keywordsEnabled) {
        return (
            <div className="rounded-lg border border-gray-200 shadow-sm">
                <div className="px-4 py-8 text-center">
                    <p className="text-sm text-gray-600">
                        Self-service keywords are not enabled. Please contact support@arwen.ai to
                        enable this feature.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 ">
                <div className=" flex flex-row justify-between items-center gap-3 w-full md:w-auto md:justify-end">
                    <div className="flex-1 min-w-[220px] md:min-w-[260px]">
                        <SearchInput
                            keyword={globalFilter ?? ''}
                            onKeywordChange={(value) => setGlobalFilter(String(value))}
                            onSearch={(value) => setGlobalFilter(String(value))}
                        />
                    </div>{' '}
                    {isLoading && (
                        <div className="text-gray-500">
                            <Spinner size={5} />
                        </div>
                    )}
                    <SimpleLink
                        to={`/account/${accountId}/keywords/create`}
                        className="whitespace-nowrap"
                    >
                        Create Keyword
                    </SimpleLink>
                </div>
            </div>
            <div className="px-4 py-4">
                {isLoading && !hasKeywords ? (
                    <div className="flex justify-center py-6">
                        <Spinner size={6} />
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto relative">
                            {!hasKeywords ? (
                                <p className="text-sm text-gray-500">
                                    No keywords have been configured.
                                </p>
                            ) : (
                                <>
                                    <div
                                        className={`transition duration-200 ${
                                            keywordsHidden
                                                ? 'blur pointer-events-none select-none'
                                                : ''
                                        }`}
                                    >
                                        <SimpleTable table={table} />
                                    </div>
                                    {keywordsHidden && (
                                        <button
                                            type="button"
                                            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/80 backdrop-blur-sm text-gray-900 text-center px-6 border border-gray-200 rounded-md shadow-sm"
                                            onClick={() => setKeywordsHidden(false)}
                                        >
                                            <span className="font-semibold">
                                                Keywords may be offensive
                                            </span>
                                            <span className="text-sm text-gray-600">
                                                Click to reveal the list.
                                            </span>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="pt-4 flex flex-row justify-between">
                            {!hasKeywords ? <div></div> : <SimplePagination table={table} />}
                            {admin && (
                                <div className="whitespace-nowrap">
                                    <AddDefaultKeywordsButton
                                        accountId={accountId}
                                        onChangeKeywords={handleKeywordDefaultsAdded}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default KeywordListTable
